import { FC, useRef } from 'react';
import Image from 'next/image';
import { FormField } from '@/components/audiences';
import { ImageIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const BASE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/bmp'];

interface IImageUploadFieldProps {
  label: string;
  helperText?: string;
  /** Current uploaded image URI (or empty string when none). */
  src: string;
  /** Name of the newly-selected (not yet uploaded) file, if any. */
  fileName?: string;
  /** Allow SVG uploads in addition to the raster formats (bot icon does, others don't). */
  allowSvg?: boolean;
  /**
   * 'row' (default) — horizontal thumbnail + choose button in a FormField wrapper.
   * 'card' — compact card with centred preview, for 3-column grids.
   */
  variant?: 'row' | 'card';
  /** Fires with the chosen File and its data-URL once validated. */
  onSelect: (file: File, dataUrl: string) => void;
  /** Fires with a user-facing message when an unsupported file is chosen. */
  onError: (message: string) => void;
}

/**
 * Single icon picker: thumbnail preview + hidden file input + choose button.
 * Supports two layouts: 'row' (default, horizontal) and 'card' (compact, for grids).
 */
const ImageUploadField: FC<IImageUploadFieldProps> = ({
  label,
  helperText,
  src,
  fileName,
  allowSvg = false,
  variant = 'row',
  onSelect,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const supportedTypes = allowSvg ? [...BASE_IMAGE_TYPES, 'image/svg+xml'] : BASE_IMAGE_TYPES;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && supportedTypes.includes(file.type)) {
      const reader = new FileReader();
      reader.onload = (e) => onSelect(file, String(e.target?.result ?? ''));
      reader.readAsDataURL(file);
    } else {
      onError('Please select a valid image file (PNG, JPEG, GIF, BMP).');
    }
  };

  const hasPreview = typeof src === 'string' && src !== '';

  const hiddenInput = (
    <input type='file' accept='image/*' onChange={handleChange} ref={inputRef} className='hidden' />
  );

  if (variant === 'card') {
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-center',
          'hover:border-slate-300 transition-colors',
        )}
      >
        {hiddenInput}
        {/* Icon preview */}
        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          title={`Change ${label}`}
          className='group relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 overflow-hidden transition-colors hover:bg-slate-200'
        >
          {hasPreview ? (
            <Image
              src={src}
              alt={`${label} preview`}
              className='h-full w-full object-cover rounded-full'
              width={64}
              height={64}
            />
          ) : (
            <ImageIcon className='h-6 w-6 text-slate-400' />
          )}
          {/* Hover overlay */}
          <span className='absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity'>
            <span className='text-[10px] font-semibold text-white'>Change</span>
          </span>
        </button>

        {/* Labels */}
        <div className='min-w-0 w-full'>
          <p className='text-xs font-semibold text-slate-800 truncate'>{label}</p>
          {helperText && (
            <p className='text-[11px] text-slate-400 leading-tight mt-0.5 truncate'>{helperText}</p>
          )}
          {fileName && (
            <p className='text-[10px] text-slate-500 mt-1 truncate'>{fileName}</p>
          )}
        </div>

        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          className='w-full px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-700 transition-colors'
        >
          Choose file
        </button>
      </div>
    );
  }

  // Default 'row' variant
  return (
    <FormField label={label} helperText={helperText}>
      <div className='flex items-center gap-4'>
        {hiddenInput}
        {hasPreview ? (
          <Image
            src={src}
            alt={`${label} preview`}
            className='w-12 h-12 rounded-full flex-shrink-0 object-cover'
            width={48}
            height={48}
          />
        ) : (
          <div className='w-12 h-12 bg-slate-200 rounded-full flex-shrink-0'></div>
        )}
        <div className='flex items-center gap-2 flex-1'>
          <button
            type='button'
            onClick={() => inputRef.current?.click()}
            className='px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium text-slate-700 transition-colors'
          >
            Choose file
          </button>
          {fileName && <span className='text-sm text-slate-600 truncate'>{fileName}</span>}
        </div>
      </div>
    </FormField>
  );
};

export default ImageUploadField;
